<template>
  <div>
    <ts-page-title
      :title="$t('employee.pageTitle')"
      :breadcrumb="[
        { text: $t('home'), href: '/' },
        {
          text: $t('employee.pageTitle'),
          active: true
        }
      ]"
    />
    <ts-panel>
      <ts-panel-wrapper class="ts-flex ts-items-center ts-justify-between">
        <div class="ts-flex ts-justify-between ts-space-x-2">
          <Button
            type="info"
            ghost
            @click="$router.push({ name: 'employee-create' })"
            >{{ $t('addNew') }}
          </Button>
          <ts-button
            outline
            color="success"
            @click.prevent="exportExcel"
            :waiting="exporting"
          >
            <i class="far fa-file-excel"></i>
            {{ $t('exportExcel') }}</ts-button
          >
          <ts-button
            v-if="true"
            class="btn-sm ml-2 mr-3"
            outline
            color="primary"
            @click="onCardPreviewPdf('pro')"
            :disabled="!employees.length > 0"
          >
            <i class="fas fa-id-card"></i> {{ $t('employeeProfile.previewCard') }}
          </ts-button>
        </div>
        <div class="ts-justify-end ts-space-x-2">
          <Input
            search
            v-model="search"
            :placeholder="$t('employee.search')"
            :style="width"
            @on-focus="() => (width = 'width: 500px')"
            @on-blur="() => (width = 'width: 300px')"
          />
          <ts-button v-tooltip="$t('filter')" @click.prevent="visible = true">
            <i class="fa fa-filter"></i>
          </ts-button>
        </div>
      </ts-panel-wrapper>
      <Table
        :columns="columns"
        :data="resources"
        size="small"
        :loading="loading"
        max-height="700"
        ref="selection"
        highlight-row
        stripe
        @on-select="onSelectCheck"
        @on-select-cancel="onSelectUncheck"
        @on-select-all="onSelectAllCheck"
        @on-select-all-cancel="onSelectAllUncheck"
      >
        <template slot-scope="{ row }" slot="photo">
          <img
            class="ts-rounded ts-h-12"
            v-lazy="{
              src: photo(row),
              error:
                row.sex == 'Male'
                  ? require('@/assets/staff-default-man.png')
                  : require('@/assets/staff-default-women.png'),
              loading: require('@/assets/Rolling-1s-200px.svg'),
              preLoad: 1.3,
              attempt: 1
            }"
          />
        </template>
        <template slot-scope="{ row }" slot="contracts_count">
          <span
            v-if="row.contracts_count > 0"
            class="ts-font-bold badge bg-blue"
          >
            {{ row.contracts_count }}
          </span>
          <span v-else> - </span>
        </template>
        <template slot-scope="{ row }" slot="branch">
          {{ row.branch ? row.branch.branch_name_en : '' }}
        </template>
        <template slot-scope="{ row }" slot="position">
          {{
            row.logical_position ? row.logical_position.position_name_en : ''
          }}
        </template>
        <template slot-scope="{ row }" slot="pc_date_id">
          {{
            row.payroll_cycle_date ? row.payroll_cycle_date.pc_date_name : ''
          }}
        </template>
        <template slot-scope="{ row }" slot="shift">
          {{ row.shift ? row.shift.shift_name : '' }}
        </template>
        <template slot-scope="{ row }" slot="status">
          <span class="badge bg-success" v-if="row.status_id == 1">{{
            $t('employee.active')
          }}</span>
          <span class="badge bg-danger" v-if="row.status_id == 2">{{
            $t('employee.resigned')
          }}</span>
          <span class="badge bg-info" v-if="row.status_id == 3">{{
            $t('employee.terminated')
          }}</span>
          <span class="badge bg-warning" v-if="row.status_id == 4">{{
            $t('employee.cancelled')
          }}</span>
          <span class="badge bg-secondary" v-if="row.status_id == 5">{{
            $t('employee.other')
          }}</span>
        </template>
        <template slot-scope="{ row }" slot="action">
          <Dropdown
            class="text-blue mr-2"
            v-tooltip="$t('employee.addContractInfo')"
            transfer
            v-if="row.contracts_count > 0"
          >
            <template>
              <Icon type="ios-add-circle" size="20" />
            </template>
            <DropdownMenu slot="list" style="margin-left: 55px">
              <DropdownItem>
                <a @click.prevent="onAddRenewContract(row)">{{
                  $t('employee.renewContract')
                }}</a>
              </DropdownItem>
              <DropdownItem>
                <a @click.prevent="onAddViewOldContract(row)">{{
                  $t('employee.viewOldContract')
                }}</a>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
          <a
            v-else
            href="#"
            class="text-info mr-2"
            @click.prevent="onAddRenewContract(row)"
            v-tooltip="$t('employee.addContractInfo')"
          >
            <Icon type="ios-add-circle" size="20" />
          </a>
          <a
            v-if="row.contracts_count > 0"
            href="#"
            class="text-blue mr-2 ts-z-30"
            @click.prevent="onPreviewPdf(row)"
            v-tooltip="$t('employee.previewContract')"
          >
            <Icon type="ios-document" size="20" />
          </a>
          <a
            href="#"
            class="text-primary"
            @click.prevent="onEdit(row)"
            v-tooltip="$t('edit')"
          >
            <Icon type="ios-create" size="20" />
          </a>
          <a
            href="#"
            class="text-danger"
            v-tooltip="$t('delete')"
            @click="onDelete(row)"
            :disabled="row._deleting"
          >
            <i class="fas fa-circle-notch fa-spin" v-if="row._deleting"></i>
            <Icon type="ios-trash" size="20" v-else />
          </a>
        </template>
      </Table>
      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-i-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-i-pagination>
      </div>
    </ts-panel>
    <Drawer
      :title="$t('filter')"
      :closable="false"
      :value="visible"
      @on-close="() => (visible = false)"
      width="300px"
    >
      <div class="mb-3">
        <label class="form-col-label control-label">{{
          $t('branchName')
        }}</label>
        <ts-branch-filter @filter="value => (branch_id = value)" />
      </div>
    </Drawer>
    <a-modal
      v-model="showAddContractInfo"
      draggable
      sticky
      scrollable
      :mask="false"
      :footer="null"
      :z-index="1022"
      :closable="true"
      :title="employee_name"
      width="950px"
    >
      <add-contract-info
        @fetchData="fetchData"
        :employees="employees"
        @cancel="cancel"
      />
    </a-modal>
    <a-modal
      v-model="showRenewContract"
      draggable
      sticky
      scrollable
      :mask="false"
      :footer="null"
      :z-index="1022"
      :closable="true"
      :title="employee_name"
      width="950px"
    >
      <add-renew-contract
        @fetchData="fetchData"
        :employees="employees"
        @cancel="cancel"
      />
    </a-modal>
    <pdf-preview
      v-model="pdf_view"
      :contract_id="contract_id"
      @cancel="close"
    />
    <pdf-preview-card
        v-model="card_pdf_view"
        :employees="employees"
        :printer="printer"
        @cancel="value => closePreviewCard(value)"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { debounce } from 'lodash'
import PdfPreviewCard from './components/pdf-preview-card'
import PdfPreview from './components/pdf-preview'

// iview
//import 'view-design/dist/styles/iview.css'
import Vue from 'vue'
import ViewUI from 'view-design'
import locale from 'view-design/dist/locale/en-US'
import { Errors } from 'form-backend-validation'
import { trim } from 'lodash'
import * as FileDownload from 'downloadjs'
import moment from 'moment'
import addContractInfo from './components/add-contract-info.vue'
import AddRenewContract from './components/add-renew-contract.vue'
import expandRow from './components/table-expand.vue'

Vue.use(ViewUI, { locale })
Vue.use(ViewUI)
export default {
  name: 'employeeIndex',
  components: {
    addContractInfo,
    PdfPreview,
    AddRenewContract,
    PdfPreviewCard
  },
  data() {
    return {
      loading: false,
      width: 'width: 300px',
      visible: false,
      exporting: false,
      branch_id: [],
      showAddContractInfo: false,
      showRenewContract: false,
      employee_name: null,
      employees: [],
      employee_id_lists: [],
      pdf_view: false,
      card_pdf_view: false,
      printer: 'pro',
      has_contract: null,
      contract_id: null,
      model: {
        employee_name_kh: null,
        employee_name_en: null,
        sex: null,
        phone: null,
        card_id: null,
        email: null,
        address: null,
        photo: null,
        position_id: null,
        hire_date: moment().format('DD-MM-YYYY'),
        probation_end_date: moment()
          .add(3, 'M')
          .format('DD-MM-YYYY'),
        status_id: null,
        status_effective_date: null,
        shift_id: null,
        pc_date_id: null,
        payment_type_id: null,
        is_tax_responsed_by_company: false,
        is_spouse_dependant: false,
        num_child_dependant: 0,
        nssf_number: null,
        ssn_no: null,
        ssn_issued_date: null,
        ssn_expiry_date: null,
        ssn_issued_at: null,
        nationality_id: null,
        date_of_birth: null,
        emp_type_id: null,
        status_reason_id: null,
        fp_serial_card: null,
        branch_id: null
      }
    }
  },
  computed: {
    ...mapState('humanResource/employee', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.humanResource.employee.search
      },
      set(newValue) {
        this.$store.commit('humanResource/employee/SET_SEARCH', newValue)
        this.$store.commit('humanResource/employee/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          type: 'expand',
          render: (h, params) => {
            return h(expandRow, {
              props: {
                row: params.row
              }
            })
          },
          width: 30
        },
        {
          type: 'selection',
          width: 60,
          align: 'center'
        },
        {
          title: this.$t('employee.numberOfContract'),
          slot: 'contracts_count',
          width: 170,
          align: 'center',
          filters: [
            {
              label: 'Yes',
              value: true
            },
            {
              label: 'No',
              value: false
            }
          ],
          filterMultiple: false,
          filterRemote(value) {
            this.has_contract = value
          }
        },
        {
          title: this.$t('employee.branch'),
          slot: 'branch',
          minWidth: 150
        },
        {
          title: this.$t('employee.photo'),
          slot: 'photo',
          minWidth: 80
        },
        {
          title: this.$t('employee.cardNumber'),
          key: 'card_id',
          minWidth: 100,
          align: 'center',
          sortable: true
        },
        {
          title: this.$t('employee.fpSerialCard'),
          key: 'fp_serial_card',
          minWidth: 150,
          align: 'center'
        },
        {
          title: this.$t('employee.nameKh'),
          key: 'employee_name_kh',
          minWidth: 170,
          sortable: true
        },
        {
          title: this.$t('employee.nameEn'),
          key: 'employee_name_en',
          minWidth: 170,
          sortable: true
        },
        {
          title: this.$t('employee.sex'),
          key: 'sex',
          minWidth: 90,
          align: 'center'
        },
        {
          title: this.$t('employee.position'),
          slot: 'position',
          minWidth: 200,
          sortable: true
        },
        {
          title: this.$t('employee.hiredDate'),
          key: 'hire_date',
          minWidth: 150,
          align: 'center',
          sortable: true
        },
        {
          title: this.$t('employee.status'),
          slot: 'status',
          minWidth: 100,
          align: 'center',
          filters: [
            {
              label: this.$t('employee.active'),
              value: 1
            },
            {
              label: this.$t('employee.resigned'),
              value: 2
            },
            {
              label: this.$t('employee.terminated'),
              value: 3
            },
            {
              label: this.$t('employee.cancelled'),
              value: 4
            },
            {
              label: this.$t('employee.other'),
              value: 5
            }
          ],
          filterMultiple: false,
          filterRemote(value) {
            this.fetchData({
              filter: {
                status_id: value
              }
            })
          }
        },
        {
          title: this.$t('employee.effectiveDate'),
          key: 'status_effective_date',
          minWidth: 150,
          align: 'center',
          sortable: true
        },
        {
          title: this.$t('employee.phone'),
          key: 'phone',
          minWidth: 150,
          align: 'right',
          sortable: true
        },
        {
          title: this.$t('employee.shift'),
          slot: 'shift',
          minWidth: 150,
          sortable: true
        },
        {
          title: this.$t('employee.payrollCycleDate'),
          slot: 'pc_date_id',
          minWidth: 200,
          sortable: true
        },
        {
          title: this.$t('actions'),
          key: 'action',
          slot: 'action',
          fixed: 'right',
          align: 'center',
          width: 150
        }
      ]
    }
  },
  methods: {
    photo(record) {
      if (record.photo) {
        return record.photo
      }

      return record.sex == 'Male'
        ? require('@/assets/staff-default-man.png')
        : require('@/assets/staff-default-women.png')
    },
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('humanResource/employee/fetch', attributes)
        .catch(error => {
          this.loading = false
          this.notice({ type: 'error', text: error.message })
        })
        .finally(() => {
          this.loading = false
        })
    },
    onEdit(record) {
      this.$store.commit('humanResource/employee/SET_EDIT_DATA', record)
      this.$router.push({
        name: 'employee-edit',
        params: { id: record.employee_id }
      })
    },
    onSelectCheck(record) {
      this.setEmployeeIdListSelected(record)
    },
    onSelectUncheck(record) {
      this.setEmployeeIdListSelected(record)
    },
    onSelectAllCheck(record) {
      this.setEmployeeIdListSelected(record)
    },
    onSelectAllUncheck(record) {
      this.setEmployeeIdListSelected(record)
    },
    handleSelectAll(status) {
      this.$refs.selection.selectAll(status)
    },
    setEmployeeIdListSelected(records) {
      this.employees = []
      if (records.length > 0) {
        records.map(row => {
          this.employees.push(row.employee_id)
        })
      }
    },
    photoEmp(record) {
      if (record.photo) {
        return record.photo + '&api_token=' + this.$store.getters.accessToken
      }

      return record.sex == 'Male' ? this.manPhoto : this.womenPhoto
    },
    async onDelete(record) {
      record._deleting = true
      this.$store
        .dispatch('humanResource/employee/destroy', record.employee_id)
        .then(response => {
          this.fetchData()
          record._deleting = false
          this.notice({
            type: response.message_id == 0 ? 'success' : 'warning',
            text: response.message
          })
        })
        .catch(error => {
          this.notice({ type: 'error', text: error.message })
          record._deleting = false
        })
    },
    exportExcel() {
      this.errors = new Errors()
      this.exporting = true

      this.$store
        .dispatch('humanResource/employee/exportExcel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.notice({ type: 'error', text: error.message })
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    },
    close() {
      this.pdf_view = false
      this.employee_id_lists = []
    },
    onAddViewOldContract(record) {
      this.employee_name =
        record.employee_name_en + ' | ' + record.employee_name_kh
      this.$store.commit('humanResource/employee/SET_EDIT_DATA', record)
      this.showAddContractInfo = true
    },
    onAddRenewContract(record) {
      this.employee_name =
        record.employee_name_en + ' | ' + record.employee_name_kh
      this.$store.commit('humanResource/employee/SET_EDIT_DATA', record)
      this.showRenewContract = true
    },
    cancel() {
      this.fetchData()
      this.employees = []
      this.showAddContractInfo = false
      this.showRenewContract = false
    },
    onPreviewPdf(record) {
      this.pdf_view = true
      this.contract_id = record.contract.contract_id
    },
    onCardPreviewPdf (type) {
        this.printer = type
        this.card_pdf_view = true
    },
    close() {
      this.pdf_view = false
      this.contract_id = null
    },
    closePreviewCard() {
      this.card_pdf_view = false
      this.employees = []
      this.handleSelectAll(false);
    },
    notice(not) {
      this.$Notice[not.type]({
        title: 'EMPLOYEE',
        desc: not.text
      })
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData({ search })
    }, 500),
    branch_id: debounce(function(value) {
      this.fetchData({ branch_id: value })
    }, 500),
    has_contract: debounce(function(value) {
      this.fetchData({
        search: this.search,
        filter: {
          has_contract: value
        }
      })
    }, 500)
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchData()
    })
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('humanResource/employee/RESET_STATE')
    next()
  }
}
</script>
<style scoped>
.text-nowrap {
  white-space: nowrap;
}
</style>
